const STORAGE_PREFIX = 'mediaProgress:v1';
const API_BASE = '/v2/student';

// Keyed by mediaItemId (not mediaSessionId) so that a browser-died recovery
// can find the entry on the next session, which has a different sessionId.
const keyFor = (userId, mediaItemId) => `${STORAGE_PREFIX}:${userId || 'anon'}:${mediaItemId}`;

const safeParse = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const readEntry = (userId, mediaItemId) => {
  if (!mediaItemId) return null;
  try {
    return safeParse(window.localStorage.getItem(keyFor(userId, mediaItemId)));
  } catch {
    return null;
  }
};

export const writeEntry = (userId, entry) => {
  if (!entry?.mediaItemId) return;
  try {
    window.localStorage.setItem(keyFor(userId, entry.mediaItemId), JSON.stringify(entry));
  } catch {
    // Quota errors, private mode — outbox is best-effort
  }
};

export const clearEntry = (userId, mediaItemId) => {
  if (!mediaItemId) return;
  try {
    window.localStorage.removeItem(keyFor(userId, mediaItemId));
  } catch {
    // ignore
  }
};

export const markEntrySent = (userId, mediaItemId, sentAt) => {
  const entry = readEntry(userId, mediaItemId);
  if (!entry) return;
  writeEntry(userId, { ...entry, sentAt });
};

export const isDirty = (entry) => !!entry && entry.updatedAt > (entry.sentAt || 0);

const buildUpdateProgressUrl = ({ courseId, subjectId, mediaItemId, mediaSessionId }) =>
  `${API_BASE}/courses/${courseId}/subjects/${subjectId}/videos/${mediaItemId}/sessions/${mediaSessionId}/update-progress`;

// Used on tab close / visibilitychange. `keepalive: true` lets the request
// outlive the document; httpOnly auth cookies still attach thanks to
// `credentials: 'include'`. Fire-and-forget — we can't observe success at
// unload time, so the caller should also mark the entry sent optimistically.
export const flushWithKeepalive = (entry) => {
  if (!entry) return false;
  const { courseId, subjectId, mediaItemId, mediaSessionId, currentTime, duration } = entry;
  if (!courseId || !subjectId || !mediaItemId || !mediaSessionId) return false;
  try {
    fetch(buildUpdateProgressUrl({ courseId, subjectId, mediaItemId, mediaSessionId }), {
      method: 'PUT',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ duration, currentTime }),
    });
    return true;
  } catch {
    return false;
  }
};
